import type { Home } from '@lmc/ts-common'

// "The site they are logged into" is not something we can actually know — there is
// no session registry — so this works off `user.home`, the member's selected site,
// which is what the backend moves and what the Carer App is pinned to.
export enum SiteRemovalKind {
  DEACTIVATE = 'deactivate',
  NONE = 'none',
  SWITCH = 'switch'
}

export type SiteRemoval =
  | { kind: SiteRemovalKind.NONE }
  | {
      kind: SiteRemovalKind.SWITCH
      currentSiteName: string
      nextSiteName: string
    }
  | {
      kind: SiteRemovalKind.DEACTIVATE
      currentSiteName: string
      removedSiteCount: number
    }

type ResolveSiteRemovalInput = {
  /** The target's selected site (`user.home`), from the profile — not form state. */
  currentHomeId?: string | null
  /** The sites they held when the form loaded. */
  initialHomes?: Array<string | { id?: string }> | null
  /** The sites left ticked. */
  nextHomes?: Array<string | { id?: string }> | null
  /** Reference list for id -> name, typically `state.data.homes`. */
  allHomes?: Home[] | null
}

const toId = (home: string | { id?: string }): string =>
  typeof home === 'string' ? home : String(home?.id ?? '')

const toIds = (homes?: Array<string | { id?: string }> | null): string[] =>
  (homes ?? []).map(toId).filter(Boolean)

// Empty when the home isn't in the reference list (stale id, or a home the viewer
// can't see) — the modal substitutes a translated fallback.
const siteName = (homeId: string, allHomes?: Home[] | null): string =>
  allHomes?.find((home) => String(home.id) === homeId)?.name ?? ''

export const resolveSiteRemoval = ({
  currentHomeId,
  initialHomes,
  nextHomes,
  allHomes
}: ResolveSiteRemovalInput): SiteRemoval => {
  const initialIds = toIds(initialHomes)
  const nextIds = toIds(nextHomes)

  if (!initialIds.length || initialIds.every((id) => nextIds.includes(id))) {
    return { kind: SiteRemovalKind.NONE }
  }

  const activeId = currentHomeId ? String(currentHomeId) : ''

  if (!nextIds.length) {
    return {
      kind: SiteRemovalKind.DEACTIVATE,
      // The site going, not `user.home`: with one site they are the same one, and
      // this still names it when the target has no selected site at all.
      currentSiteName: siteName(initialIds[0], allHomes),
      removedSiteCount: initialIds.length
    }
  }

  if (
    !activeId ||
    !initialIds.includes(activeId) ||
    nextIds.includes(activeId)
  ) {
    return { kind: SiteRemovalKind.NONE }
  }

  return {
    kind: SiteRemovalKind.SWITCH,
    currentSiteName: siteName(activeId, allHomes),
    // Must match the backend's choice in users/update.ts — first site still held.
    nextSiteName: siteName(nextIds[0], allHomes)
  }
}
